import { getConsoleAccessToken } from "@/lib/auth/consoleAccessToken";
import {
  endConsoleSessionAndLogOut,
  handleConsoleUnauthorized,
  UNAUTHORIZED_OUTCOME,
} from "@/lib/auth/consoleSessionLost";
import type { PublicRuntimeConfig } from "@/lib/config/publicRuntimeConfig";
import {
  csrfCustomHeaderRecord,
  isMutatingHttpMethod,
} from "./csrfCustomHeader";

const consoleApiUrl = (config: PublicRuntimeConfig, path: string): string =>
  new URL(path, config.consoleServiceOrigin).toString();

const withBearer = (init: RequestInit | undefined): RequestInit => {
  const headers = new Headers(init?.headers);
  const token = getConsoleAccessToken();
  if (token != null) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (isMutatingHttpMethod(init?.method)) {
    for (const [name, value] of Object.entries(csrfCustomHeaderRecord())) {
      headers.set(name, value);
    }
  }
  return { ...init, headers };
};

/** Never settles: the page is being replaced by Cognito. */
const pendingForRedirect = (): Promise<Response> =>
  new Promise<Response>(() => undefined);

/**
 * `fetch` against the console API with a Bearer token. On a 401 the shared
 * session handling renews the token (or finds it already renewed) and the request
 * is retried once; a Cognito rejection sends the user to login, and a fresh token
 * being rejected logs them out of Cognito. When renewing is not possible right
 * now (offline) the 401 is returned as is.
 */
export const consoleFetch = async (
  config: PublicRuntimeConfig,
  path: string,
  init?: RequestInit,
): Promise<Response> => {
  const url = consoleApiUrl(config, path);
  const sentToken = getConsoleAccessToken();
  const first = await fetch(url, withBearer(init));
  if (first.status !== 401) {
    return first;
  }
  const outcome = await handleConsoleUnauthorized(config, sentToken);
  if (outcome === UNAUTHORIZED_OUTCOME.LOST) {
    return pendingForRedirect();
  }
  if (outcome === UNAUTHORIZED_OUTCOME.UNAVAILABLE) {
    return first;
  }
  const retry = await fetch(url, withBearer(init));
  if (retry.status === 401) {
    return (await endConsoleSessionAndLogOut(config))
      ? pendingForRedirect()
      : retry;
  }
  return retry;
};
